import React, { useCallback, useMemo, useRef, useState } from "react";

import SimpleIntersectionObserver from "@hl/base-components/lib/SimpleIntersectionObserver";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import MarketplaceFilterSortBar from "@hl/shared-features/lib/features/marketplace/components/MarketplaceFilterSortBar";
import { sortingOptionsDefault } from "@hl/shared-features/lib/utils/sortingOptions";
import { Center, Group, Loader, SimpleGrid, Stack, Modal } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";

import { _CollectionType } from "apollo/graphql.generated";

import { useCollectionTokensQuery } from "../../../../../../features/tokens/sections/ManageToken/queries.graphql.generated";

import TokenCard from "./TokenCard";

type Props = {
  collection: {
    id: string;
    address: string;
    chainId: number;
    collectionType: _CollectionType;
    name: string;
    editionId?: number | null;
  };
  isOpened: boolean;
  onClose: () => void;
};

export const CollectorsChoiceCreatorReserves = ({
  collection,
  isOpened,
  onClose,
}: Props) => {
  const [searchString, setSearchString] = useState<string>("");
  const [debouncedSearchString] = useDebouncedValue(searchString, 250);
  const [sorting, setSorting] = useState<string | null>(
    sortingOptionsDefault[0].value
  );
  const selectedSort = useMemo(
    () =>
      sortingOptionsDefault.find(
        (sortingOption) => sortingOption.value === sorting
      ),
    [sorting]
  );

  const bodyRef = useRef(null);

  const {
    data: tokensData,
    loading,
    fetchMore,
  } = useCollectionTokensQuery({
    variables: {
      tokenAddress: collection.address,
      after: null,
      first: 12,
      withListings: null,
      withOffers: null,
      tokenNameOrId: debouncedSearchString,
      sortBy: selectedSort!.sortBy,
      sortDirection: selectedSort!.sortDirection,
      minted: null,
      attributes: null,
      chainId: collection.chainId,
    },
    skip: !isOpened,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const pageInfo = tokensData?.getCollectionTokens?.pageInfo;
  const nextCursor = pageInfo?.endCursor;

  const loadMore = useCallback(() => {
    fetchMore({
      variables: {
        after: nextCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        fetchMoreResult.getCollectionTokens.edges = [
          ...(previousResult?.getCollectionTokens?.edges ?? []),
          ...(fetchMoreResult?.getCollectionTokens?.edges ?? []),
        ];
        return fetchMoreResult;
      },
    });
  }, [fetchMore, nextCursor]);

  const tokens = tokensData?.getCollectionTokens?.edges;

  return (
    <Modal
      opened={isOpened}
      onClose={onClose}
      size="60%"
      title={`Select a token from ${collection.name}`}
    >
      <Stack ref={bodyRef} spacing="xl">
        <MarketplaceFilterSortBar
          setSorting={setSorting}
          sorting={sorting}
          hasFilterToggle={false}
          sortingOptions={sortingOptionsDefault}
          setSearchString={setSearchString}
          searchString={searchString}
          containerConfig={{
            top: 0,
            zIndex: 99,
          }}
        />
        <Group spacing={40} noWrap align="flex-start">
          <Stack w="100%">
            {!tokens?.length ? (
              loading ? (
                <Loading />
              ) : (
                <NoResults
                  description={"Try adjusting your filter settings"}
                  title={"No tokens found"}
                />
              )
            ) : (
              <Stack align="center">
                <SimpleGrid
                  cols={4}
                  spacing={40}
                  verticalSpacing={56}
                  breakpoints={[
                    { maxWidth: "lg", cols: 3 },
                    { maxWidth: "md", cols: 2 },
                    { maxWidth: "sm", cols: 1, verticalSpacing: 40 },
                  ]}
                >
                  {tokens?.map((token) => (
                    <TokenCard
                      key={token.id}
                      token={token}
                      collectionId={collection.id}
                      collectionType={collection.collectionType}
                      collectionEditionId={collection.editionId || 0}
                    />
                  ))}
                </SimpleGrid>
                {!loading && !!nextCursor && (
                  <SimpleIntersectionObserver
                    onIntersection={(e) => {
                      if (e.isIntersecting) {
                        loadMore();
                      }
                    }}
                  >
                    <pre>
                      <Center>
                        <Loading />
                      </Center>
                    </pre>
                  </SimpleIntersectionObserver>
                )}
              </Stack>
            )}
          </Stack>
        </Group>
      </Stack>
    </Modal>
  );
};

const Loading = () => (
  <Center p="lg" w="100%">
    <Loader />
  </Center>
);
